import React, { useEffect, useState } from "react";
import { Grid, CircularProgress, Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { SearchBarComponent } from "../../../component/atom";
import {QuizMarkCard,} from "../../../component/molecule";
import HeadingComponent from "../../../component/atom/Headings/Heading";
import DropDownComponent from "../../../component/atom/Inputs/DropDown";
import DocumentBreadcrumbs from "../../../component/molecule/Document/BreadCrumb";
import { connect } from "react-redux";
import { Actions } from "../../../core/modules/Actions";
import _ from "lodash";
import { getText, getLocalStorageThumbnail, getThumbnailSrc, getBaseUrl, safeBuildThumbnailUrl } from "../../../core/Constant";

const extractCourseId = (val) => {
  if (!val) return "";
  if (typeof val === "object") {
    return String(val.course_id || val.id || val.value || "");
  }
  return String(val).replace(/^"(.*)"$/, "$1").trim();
};

const getSessionData = () => {
  const savedCourse =
    sessionStorage.getItem("quiz_course") ||
    sessionStorage.getItem("std_quiz_selected_course");
  
  let courseId = "";
  if (savedCourse) {
    try {
      courseId = extractCourseId(JSON.parse(savedCourse));
    } catch {
      courseId = extractCourseId(savedCourse);
    }
  }

  let trail = [{ id: null, name: "" }];
  const savedTrail =
    sessionStorage.getItem("quiz_breadcrumb") ||
    sessionStorage.getItem("std_quiz_breadcrumb_trail");

  if (savedTrail) {
    try {
      const parsed = JSON.parse(savedTrail);
      if (Array.isArray(parsed) && parsed.length > 0) {
        trail = parsed;
      }
    } catch (e) {
      console.error("Error parsing saved breadcrumbs:", e);
    }
  }

  const lastCrumb = trail[trail.length - 1];
  const parentId = lastCrumb?.id ?? null;

  return { courseId, trail, parentId };
};

const QuizStartScreen = ({
  stdquizList,
  stdgetQuizlist,
  quizStateChange,
  getCourseList,
  getStdCourseList,
  stdCourseList,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const sessionData = getSessionData();
  const [course, setCourse] = useState(sessionData.courseId);
  const [breadcrumbTrail, setBreadcrumbTrail] = useState(sessionData.trail);

  const [directories, setDirectories] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [newcourseList, setCourseList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Helper to get selected course object or name
  const getSelectedCourseName = (courseId, list = newcourseList) => {
    const cleanId = extractCourseId(courseId);
    const found = list.find((item) => String(item.id) === String(cleanId));
    return found?.name || "Course";
  };

  const isQuizInProgress = (quiz) => {
    const startedAt = quiz?.is_started;
    const finishedAt = quiz?.is_finished;
    return Number(startedAt) === 1 && Number(finishedAt) === 0;
  };

  const syncStorage = (activeCourse, activeTrail) => {
    const cleanId = extractCourseId(activeCourse);
    if (cleanId) {
      sessionStorage.setItem("quiz_course", JSON.stringify(cleanId));
      sessionStorage.setItem("std_quiz_selected_course", JSON.stringify(cleanId));
      localStorage.setItem("firstcourse", cleanId);
    }
    if (activeTrail && Array.isArray(activeTrail)) {
      sessionStorage.setItem("quiz_breadcrumb", JSON.stringify(activeTrail));
      sessionStorage.setItem("std_quiz_breadcrumb_trail", JSON.stringify(activeTrail));
    }
  };

  const fetchQuizzes = async (courseId, parentId = null, search = "") => {
    const cleanCourseId = extractCourseId(courseId);
    if (!cleanCourseId) return;

    setLoading(true);

    const params = new URLSearchParams();

    parentId !== null && params.append("parent_id", parentId);
    search.trim() && params.append("search", search);

    const queryString = `${cleanCourseId}${params.toString() ? `?${params}` : ""}`;

    try {
      await stdgetQuizlist(queryString);
    } catch (error) {
      console.error("Error fetching quizzes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    quizStateChange(false);
    getCourseList();
    getStdCourseList();
  }, []);

  useEffect(() => {
    if (!stdCourseList) return;

    const list = stdCourseList.map((item) => ({
      id: String(item.course_id || item.id),
      course_id: String(item.course_id || item.id),
      name: item.course_name || item.name,
    }));
    setCourseList(list);

    if (list.length === 0) {
      setCourse("");
      setDirectories([]);
      setQuizzes([]);
      setBreadcrumbTrail([{ id: null, name: "" }]);
      return;
    }

    const currentSession = getSessionData();
    
    const isCourseInList = list.some(
      (item) => String(item.id) === String(currentSession.courseId)
    );

    let activeCourseId = "";

    if (isCourseInList) {
      activeCourseId = currentSession.courseId;
    } else {
      activeCourseId = "";
    }

      setCourse(activeCourseId);

    if (!activeCourseId) {
      setDirectories([]);
      setQuizzes([]);
      setBreadcrumbTrail([{ id: null, name: "" }]);
      return;
    }

    const activeTrail = isCourseInList
      ? currentSession.trail
      : [{ id: null, name: "" }];

    const courseName = getSelectedCourseName(activeCourseId, list);
    const updatedTrail = activeTrail.map((crumb, idx) =>
      idx === 0 ? { ...crumb, name: courseName } : crumb
    );

    setBreadcrumbTrail(updatedTrail);
    syncStorage(activeCourseId, updatedTrail);

    const parentIdToUse = isCourseInList ? currentSession.parentId : null;
    fetchQuizzes(activeCourseId, parentIdToUse, searchInput);
  }, [stdCourseList]);

  useEffect(() => {
    const rawData = stdquizList?.data || stdquizList || {};
    const dirList = rawData?.quiz_directories || [];
    const quizList = rawData?.quizzes || (Array.isArray(rawData) ? rawData : []);

    setDirectories(dirList);
    setQuizzes(quizList);
  }, [stdquizList]);

  // Handle dropdown course selection
  const onCourseChange = (e) => {
    const selectedValue = extractCourseId(e?.target?.value || e);
    if (!selectedValue) {
      setCourse("");
      setDirectories([]);
      setQuizzes([]);
      return;
    }

    setCourse(selectedValue);

    const courseName = getSelectedCourseName(selectedValue);
    const resetTrail = [{ id: null, name: courseName }];

    setBreadcrumbTrail(resetTrail);
    setSearchInput("");

    syncStorage(selectedValue, resetTrail);
    fetchQuizzes(selectedValue, null, "");
    quizStateChange(false);
  };

  // Open Folder
  const handleFolderClick = (folder) => {
    const folderId = folder.id ?? folder.directory_id;
    const activeCourseId = extractCourseId(course);

    if (activeCourseId && folderId !== undefined) {
      const updatedTrail = [
        ...breadcrumbTrail,
        { id: folderId, name: folder.directory_name },
      ];

      setBreadcrumbTrail(updatedTrail);
      setSearchInput("");
      syncStorage(activeCourseId, updatedTrail);
      fetchQuizzes(activeCourseId, folderId, "");
    }
  };

  // Click Breadcrumb
  const handleBreadcrumbClick = (index, crumb) => {
    const activeCourseId = extractCourseId(course);
    const updatedTrail = breadcrumbTrail.slice(0, index + 1);

    setBreadcrumbTrail(updatedTrail);
    setSearchInput("");
    syncStorage(activeCourseId, updatedTrail);

    if (activeCourseId) {
      fetchQuizzes(activeCourseId, crumb.id, "");
    }
  };

  const navigateToQuizMain = (value, resume = false) => {
    localStorage.removeItem("currentQuestion");

    if (!resume) {
      quizStateChange(false);
    }

    const activeCourseId = extractCourseId(course);
    syncStorage(activeCourseId, breadcrumbTrail);

    navigate("/quiz-main", {
      state: {
        quizData: value,
        resume,
        breadcrumbTrail,
        selectedCourse: activeCourseId,
      },
    });
  };

  const navigateToAttempts = (value) => {
    const activeCourseId = extractCourseId(course);
    syncStorage(activeCourseId, breadcrumbTrail);

    navigate("/quiz-attempt", {
      state: {
        quiz: value,
        breadcrumbTrail,
        selectedCourse: activeCourseId,
      },
    });
  };

  const handleSearch = (searchedValue) => {
    setSearchInput(searchedValue);
    if (!course) return;
    const currentSession = getSessionData();
    fetchQuizzes(course, currentSession.parentId, searchedValue);
  };

  const isEmpty = !loading && directories.length === 0 && quizzes.length === 0;

  return (
    <>
      <Grid className="main-screen-container" container flexDirection={"column"} rowSpacing={1} mt={2}>
        <Grid item>
          <Grid container justifyContent={"space-between"}>
            <Grid item>
              <HeadingComponent
                fontweigth={600}
                size={"26px"}
                color={"#2d3945"}
                text={"Quizzes"}
              />
            </Grid>
            <Grid item>
              <Grid container alignItems={"center"}>
                <Grid item>
                  <DropDownComponent
                    isShowPlaceholder={true}
                    isShowZero={newcourseList.length === 0}
                    initialValue="Select Course"
                    onchange={onCourseChange}
                    radius={"15px"}
                    list={newcourseList}
                    selectedValue={course}
                    fixedWidth="100%"
                  />
                </Grid>
                <Grid item>
                  <SearchBarComponent
                    value={searchInput}
                    onchange={(e) => handleSearch(getText(e))}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Breadcrumbs */}
        {breadcrumbTrail.length > 1 && (
          <Grid item>
            <DocumentBreadcrumbs
              breadcrumbTrail={breadcrumbTrail}
              onBreadcrumbClick={handleBreadcrumbClick}
            />
          </Grid>
        )}

        {/* Loader Display */}
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isEmpty && (
              <>
                <p style={{ marginTop: "15px" }} className="week-text">
                  No Quizzes found
                </p>
                <p style={{ marginTop: "1%" }} className="week-text">
                  Please Select Your Course
                </p>
              </>
            )}

            {/* Render Folders & Quizzes */}
            <Grid container sx={{ gap: 2, mt: 1 }}>
              {directories.map((folder, index) => {
                const folderImage = getThumbnailSrc(folder?.thumbnail, "folder_thumbnail");

                return (
                  <Grid key={`folder-${folder.id || index}`} item>
                    <QuizMarkCard
                      isFolder={true}
                      title={folder?.directory_name}
                      thumbnail={folderImage}
                      onViewFolder={() => handleFolderClick(folder)}
                    />
                  </Grid>
                );
              })}

              {quizzes.map((value, count) => {
                const attemptsMade = value?.quiz_status?.[0]?.attempts ?? 0;
                const leftattempts = (value?.no_of_attempts || 0) - attemptsMade;
                const status = attemptsMade === 0;
                const canResume = isQuizInProgress(value);

                const apiThumbnail = value?.thumbnail || value?.image;
                const quizImage = getThumbnailSrc(apiThumbnail, "quiz_thumbnail");

                return (
                  <Grid key={`quiz-${value.id || count}`} item>
                    <QuizMarkCard
                      isFolder={false}
                      title={value?.name}
                      thumbnail={quizImage}
                      status={status}
                      onViewAttempsts={() => navigateToAttempts(value)}
                      attempts={leftattempts}
                      duration={value?.duration}
                      date={""}
                      isResume={canResume}
                      onViewQuiz={() => navigateToQuizMain(value, canResume)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default connect(
  (state) => ({
    stdquizList: state.quizes.get("stdquizList"),
    courseList: state.course.get("commonCourseList"),
    stdCourseList: state.course.get("stdCourseList"),
  }),
  {
    stdgetQuizlist: Actions.quizes.stdgetQuizlist,
    quizStateChange: Actions.quizes.quizStateChange,
    getCourseList: Actions.course.getCourseList,
    getStdCourseList: Actions.course.getStdCourseList,
  }
)(QuizStartScreen);